import { ProductionPrograms } from '@/features/digitalPrograms/ProductionPrograms'
import { invitationState, isSelectedParticipant } from '@/types/selectionWorkflow'
import { InvitationProgress, InvitationStatus } from '../components/InvitationStatus'
import { reconcileProductionInvitationFn } from '@/lib/firebase/functions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ProductionRequirementsSettings } from '../components/ProductionRequirementsSettings'
import { DeliverableReviewDialog } from '../components/DeliverableReviewDialog'
import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ExternalLink,
  Theater,
  ClipboardList,
  Users,
  ArrowLeft,
  Calendar,
  Inbox,
  CheckCircle2,
  Hourglass,
  Pencil,
  FilePlus2,
  TicketPlus,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { EditProductionDialog } from '../components/EditProductionDialog'
import { ParticipantPackageSettingsCard } from '../components/ParticipantPackageSettingsCard'
import { TechSubmissionViewModal } from '../components/TechSubmissionViewModal'
import { sendAcceptanceReminderNowFn, sendTechReminderNowFn } from '@/lib/firebase/functions'
import { Bell, BellRing, Wrench } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useProduction } from '../hooks/useProduction'
import { useProductionSubmissionStats } from '../hooks/useProductionSubmissionStats'
import { useCurationSubmissions } from '../hooks/useCurationSubmissions'
import type { ProductionStatus } from '@/types'

const STATUS_LABEL: Record<ProductionStatus, string> = {
  planning: 'Planning',
  intake_open: 'Intake Open',
  curating: 'Curating',
  confirmed: 'Confirmed',
  completed: 'Completed',
}

export function ProductionCommandCenterPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { production, loading, error, refresh } = useProduction(id)
  const [editOpen, setEditOpen] = useState(false)
  const { stats, loading: statsLoading } = useProductionSubmissionStats(id ?? null)
  const {
    submissions,
    loading: submissionsLoading,
    current: submissionsCurrent,
    liveError: submissionsError,
    refresh: refreshSubmissions,
  } = useCurationSubmissions(id ?? null)

  const confirmed = submissions.filter(
    (s) => s.status === 'accepted' || invitationState(s) !== 'not_sent'
  )
  const [reviewingSubmission, setReviewingSubmission] = useState<string | null>(null)
  const [reconciling, setReconciling] = useState<string | null>(null)
  const [providerMessageId, setProviderMessageId] = useState('')
  const [reconcileResult, setReconcileResult] = useState('')
  const [reconcileBusy, setReconcileBusy] = useState(false)
  const [remindingId, setRemindingId] = useState<string | null>(null)
  const [reminderToast, setReminderToast] = useState<string | null>(null)

  async function sendReminder(submissionId: string, kind: 'acceptance' | 'tech') {
    setRemindingId(`${kind}:${submissionId}`)
    setReminderToast(null)
    try {
      const fn = kind === 'acceptance' ? sendAcceptanceReminderNowFn : sendTechReminderNowFn
      const r = await fn({ submissionId })
      const { sent, skipped, failed, pending, uncertain } = r.data
      const label = kind === 'acceptance' ? 'Acceptance' : 'Tech'
      if (sent > 0) setReminderToast(`${label} reminder accepted by the email provider.`)
      else if (pending > 0) setReminderToast(`${label} reminder queued for automatic recovery.`)
      else if (uncertain > 0)
        setReminderToast(
          `${label} reminder needs reconciliation in the email provider logs before another send.`
        )
      else if (skipped > 0)
        setReminderToast(`${label} skipped — already responded or hit the 3-reminder cap.`)
      else if (failed > 0) {
        setReminderToast(
          withErrorCode(
            `${label} reminder could not be sent. Please check the logs and try again.`,
            'production/reminder-failed'
          )
        )
      }
      refreshSubmissions()
    } catch (err) {
      setReminderToast(formatUserFacingError(err, 'Reminder failed.'))
    } finally {
      setRemindingId(null)
    }
  }

  const hasTechForm = !!production?.techApplicationId
  const [viewingTechSubmission, setViewingTechSubmission] = useState<{
    techSubmissionId: string
    intakeApplicantName: string
  } | null>(null)

  if (loading) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !production) {
    return (
      <div className="space-y-2 py-6">
        <Link
          to="/admin/productions"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Productions
        </Link>
        <p className="text-destructive">
          {error ?? withErrorCode('Production not found.', 'productions/not-found')}
        </p>
      </div>
    )
  }

  const productionId = production.id

  function handleTabChange(value: string) {
    if (value === 'intake') {
      navigate(`/admin/productions/${productionId}/curate`)
    } else {
      setSearchParams({ tab: value }, { replace: true })
    }
  }

  return (
    <div className="space-y-6 py-2">
      <div className="space-y-2">
        <Link
          to="/admin/productions"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Productions
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{production.name}</h1>
            <p className="text-sm text-muted-foreground">
              {production.venue} · {formatDateRange(production.startDate, production.endDate)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="default">{STATUS_LABEL[production.status]}</Badge>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
          </div>
        </div>
      </div>

      <InvitationProgress
        production={production}
        submissions={submissions}
        loading={submissionsLoading}
        current={submissionsCurrent}
        error={submissionsError}
      />
      <Tabs
        value={searchParams.get('tab') === 'participants' ? 'participants' : 'overview'}
        onValueChange={handleTabChange}
      >
        <TabsList>
          <TabsTrigger value="overview">
            <Theater className="mr-1 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="intake">
            <ClipboardList className="mr-1 h-4 w-4" />
            Intake
          </TabsTrigger>
          <TabsTrigger value="participants">
            <Users className="mr-1 h-4 w-4" />
            Participants
          </TabsTrigger>
          <TabsTrigger value="program">Digital Program</TabsTrigger>
        </TabsList>

        <TabsContent value="program">
          <ProductionPrograms productionId={production.id} />
        </TabsContent>
        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Create linked records</CardTitle>
              <CardDescription>
                Start from this production to inherit shared names, dates, venue, and linkage.
                Standalone event and application creation remains available from their admin
                sections.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/admin/events/new?productionId=${encodeURIComponent(production.id)}`}>
                  <TicketPlus className="mr-1.5 h-4 w-4" />
                  Create linked event
                </Link>
              </Button>
              {production.intakeApplicationId ? (
                <Button asChild variant="outline" size="sm">
                  <Link to={`/admin/applications/${production.intakeApplicationId}/edit`}>
                    <ClipboardList className="mr-1.5 h-4 w-4" />
                    Edit intake application
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={`/admin/applications/new?productionId=${encodeURIComponent(production.id)}`}
                  >
                    <FilePlus2 className="mr-1.5 h-4 w-4" />
                    Create linked application
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Stat tiles */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              icon={Calendar}
              label="Show Date"
              value={countdownValue(production.startDate)}
              hint={countdownHint(production.startDate)}
            />
            <StatTile
              icon={Inbox}
              label="Total Submissions"
              value={statsLoading ? '—' : String(stats.total)}
              hint="across all statuses"
            />
            <StatTile
              icon={CheckCircle2}
              label="Invitations processed"
              value={String(confirmed.length)}
              hint="see delivery and response status"
            />
            <StatTile
              icon={Hourglass}
              label="Awaiting Review"
              value={statsLoading ? '—' : String(stats.pending + stats.reviewing)}
              hint="pending + reviewing"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Type" value={capitalize(production.type)} />
                <Row label="Slug" value={<span className="font-mono">/{production.slug}</span>} />
                <Row label="Status" value={STATUS_LABEL[production.status]} />
                <Row
                  label="Drive"
                  value={
                    production.driveLink ? (
                      <a
                        href={production.driveLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-primary hover:underline"
                      >
                        Open <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Submission Stats</CardTitle>
                <CardDescription>Across all linked intake submissions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {statsLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : (
                  <>
                    <Row label="Pending" value={stats.pending} />
                    <Row label="Reviewing" value={stats.reviewing} />
                    <Row label="Accepted" value={stats.accepted} />
                    <Row label="Rejected" value={stats.rejected} />
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <ProductionRequirementsSettings
            productionId={production.id}
            production={production}
            onSaved={refresh}
          />
          <ParticipantPackageSettingsCard
            productionId={production.id}
            production={production}
            onSaved={refresh}
          />
        </TabsContent>

        {/* Participants */}
        <TabsContent value="participants">
          {submissionsLoading ? (
            <Card>
              <CardContent className="py-8">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ) : confirmed.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                <Users className="h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium">No invitations have been initiated yet.</p>
                <p className="max-w-md text-xs text-muted-foreground">
                  Participants appear here once they're accepted and finalized through the curation
                  workspace.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="space-y-2 overflow-x-auto p-0">
                {reminderToast && (
                  <div className="border-b bg-muted/30 px-4 py-2 text-xs">{reminderToast}</div>
                )}
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Name</th>
                      <th className="px-4 py-2 font-medium">Email</th>
                      <th className="px-4 py-2 font-medium">Invitation / Response</th>
                      {!!production.participantRequirements?.length && (
                        <th className="px-4 py-2 font-medium">Required materials</th>
                      )}
                      {hasTechForm && <th className="px-4 py-2 font-medium">Tech</th>}
                      <th className="px-4 py-2 font-medium">Reminders</th>
                      <th className="px-4 py-2 font-medium">Last Contact</th>
                      <th className="px-4 py-2 font-medium text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {confirmed.map((s) => {
                      const fullName =
                        `${s.applicantInfo.firstName} ${s.applicantInfo.lastName}`.trim() ||
                        'Unknown'
                      const status: 'accepted' | 'declined' | 'pending' =
                        s.participantAcceptedAt &&
                        !s.participantWithdrawnAt &&
                        !s.participantDeclinedAt &&
                        s.status === 'accepted'
                          ? 'accepted'
                          : !isSelectedParticipant(s)
                            ? 'declined'
                            : 'pending'
                      const remindersSent = s.remindersSent ?? 0
                      const techRemindersSent = s.techRemindersSent ?? 0
                      const techComplete = !!s.techCompletedAt
                      const required =
                        production.participantRequirements?.filter((r) => r.required) ?? []
                      const outstanding = required.filter((r) => {
                        const delivery = s.deliverableStates?.[r.id]
                        return (
                          !delivery ||
                          delivery.requirementVersion !==
                            production.participantRequirementsVersion ||
                          delivery.status === 'needs_changes'
                        )
                      })
                      const ready =
                        status === 'accepted' &&
                        required.length > 0 &&
                        required.every(
                          (r) =>
                            s.deliverableStates?.[r.id]?.status === 'verified' &&
                            s.deliverableStates[r.id]?.requirementVersion ===
                              production.participantRequirementsVersion
                        )
                      const materialStatus = ready
                        ? 'Verified and ready'
                        : status === 'declined'
                          ? 'Not participating'
                          : invitationState(s) !== 'sent'
                            ? 'Awaiting invitation'
                            : status !== 'accepted'
                              ? 'Awaiting artist acceptance'
                              : outstanding.some((r) => Date.parse(r.dueAt) < Date.now())
                                ? 'Overdue — action needed'
                                : outstanding.length
                                  ? 'Missing / changes needed'
                                  : 'Awaiting staff review'

                      const techState: 'complete' | 'pending' | 'na' = !hasTechForm
                        ? 'na'
                        : techComplete
                          ? 'complete'
                          : 'pending'

                      const lastTouch =
                        s.lastTechReminderSentAt ?? s.lastReminderSentAt ?? s.confirmedAt

                      const canRemindAcceptance =
                        submissionsCurrent &&
                        !production.isArchived &&
                        production.status !== 'completed' &&
                        invitationState(s) === 'sent' &&
                        status === 'pending' &&
                        remindersSent < 3
                      const canRemindTech =
                        submissionsCurrent &&
                        !production.isArchived &&
                        production.status !== 'completed' &&
                        invitationState(s) === 'sent' &&
                        status === 'accepted' &&
                        hasTechForm &&
                        (required.length ? outstanding.length > 0 : !techComplete) &&
                        techRemindersSent < 3
                      const acceptanceBusy = remindingId === `acceptance:${s.id}`
                      const techBusy = remindingId === `tech:${s.id}`

                      const totalReminders = remindersSent + techRemindersSent

                      return (
                        <tr key={s.id}>
                          <td className="px-4 py-2 font-medium">{fullName}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {s.applicantInfo.email}
                          </td>
                          <td className="px-4 py-2">
                            <InvitationStatus submission={s} showResponse={false} />
                            {invitationState(s) === 'sent' && <ResponseBadge status={status} />}
                            {s.invitationDeliveryStatus === 'uncertain' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setReconciling(s.id)
                                  setProviderMessageId('')
                                  setReconcileResult('')
                                }}
                              >
                                Reconcile
                              </Button>
                            )}
                          </td>
                          {!!production.participantRequirements?.length && (
                            <td className="px-4 py-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setReviewingSubmission(s.id)}
                              >
                                {materialStatus}
                              </Button>
                            </td>
                          )}
                          {hasTechForm && (
                            <td className="px-4 py-2">
                              {status === 'declined' ? (
                                <span className="text-muted-foreground">Not required</span>
                              ) : techComplete && s.techSubmissionId ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setViewingTechSubmission({
                                      techSubmissionId: s.techSubmissionId!,
                                      intakeApplicantName: fullName,
                                    })
                                  }
                                  className="cursor-pointer"
                                  title="View tech submission"
                                >
                                  <TechBadge
                                    state={techState}
                                    acceptedYet={status === 'accepted'}
                                  />
                                </button>
                              ) : (
                                <TechBadge state={techState} acceptedYet={status === 'accepted'} />
                              )}
                            </td>
                          )}
                          <td className="px-4 py-2 text-muted-foreground">{totalReminders} sent</td>
                          <td className="px-4 py-2 text-muted-foreground">{formatTs(lastTouch)}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end gap-2">
                              {canRemindAcceptance && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={acceptanceBusy}
                                  onClick={() => sendReminder(s.id, 'acceptance')}
                                  title={`Send acceptance reminder ${remindersSent + 1} of 3`}
                                >
                                  {acceptanceBusy ? (
                                    <Bell className="mr-1 h-3.5 w-3.5 animate-pulse" />
                                  ) : (
                                    <BellRing className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  Remind
                                </Button>
                              )}
                              {canRemindTech && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={techBusy}
                                  onClick={() => sendReminder(s.id, 'tech')}
                                  title={`Send tech reminder ${techRemindersSent + 1} of 3`}
                                >
                                  {techBusy ? (
                                    <Wrench className="mr-1 h-3.5 w-3.5 animate-pulse" />
                                  ) : (
                                    <Wrench className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  Remind Tech
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <EditProductionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        productionId={id ?? ''}
        production={production}
        onSaved={refresh}
      />

      <Dialog
        open={!!reconciling}
        onOpenChange={(open) => {
          if (!open) setReconciling(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reconcile invitation</DialogTitle>
            <DialogDescription>
              Find this invitation in the Resend email logs and enter its message ID. We verify its
              recipient and saved contents before recording provider acceptance. This action does
              not send an email.
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="Provider message ID"
            value={providerMessageId}
            onChange={(event) => setProviderMessageId(event.target.value)}
            placeholder="Resend message ID"
          />
          {reconcileResult && <p className="text-sm">{reconcileResult}</p>}
          <Button
            disabled={!providerMessageId || reconcileBusy}
            onClick={async () => {
              if (!reconciling) return
              setReconcileBusy(true)
              try {
                const result = await reconcileProductionInvitationFn({
                  submissionId: reconciling,
                  providerMessageId,
                })
                setReconcileResult(
                  `Reconciled. Latest provider event: ${result.data.providerEvent}`
                )
                refreshSubmissions()
              } catch (error) {
                setReconcileResult(
                  formatUserFacingError(error, 'Could not reconcile this invitation.')
                )
              } finally {
                setReconcileBusy(false)
              }
            }}
          >
            Check provider record
          </Button>
        </DialogContent>
      </Dialog>
      <DeliverableReviewDialog
        submissionId={reviewingSubmission}
        onClose={() => setReviewingSubmission(null)}
        onSaved={refreshSubmissions}
      />
      <TechSubmissionViewModal
        techSubmissionId={viewingTechSubmission?.techSubmissionId ?? null}
        intakeApplicantName={viewingTechSubmission?.intakeApplicantName ?? ''}
        onClose={() => setViewingTechSubmission(null)}
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

function formatDateRange(start: string, end?: string): string {
  if (!start) return ''
  const startStr = formatDate(start)
  if (!end) return startStr
  return `${startStr} – ${formatDate(end)}`
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function TechBadge({
  state,
  acceptedYet,
}: {
  state: 'complete' | 'pending' | 'na'
  acceptedYet: boolean
}) {
  if (state === 'na') {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  if (state === 'complete') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700">
        Complete
      </span>
    )
  }
  // Pending — distinguish "blocked on acceptance" from "actively pending"
  if (!acceptedYet) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        Awaiting accept
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700">
      Pending
    </span>
  )
}

function ResponseBadge({ status }: { status: 'accepted' | 'declined' | 'pending' }) {
  if (status === 'accepted') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700">
        Accepted
      </span>
    )
  }
  if (status === 'declined') {
    return (
      <span className="inline-flex items-center rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">
        Declined
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700">
      Pending
    </span>
  )
}

function formatTs(ts: { toMillis?: () => number } | undefined | null): string {
  const ms = ts?.toMillis?.()
  if (!ms) return '—'
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function StatTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon
  label: string
  value: string
  hint?: string
}) {
  return (
    <Card>
      <CardContent className="space-y-1 py-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </div>
        <div className="text-2xl font-semibold leading-none">{value}</div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  )
}

/**
 * Days between today and the production's start date, ISO YYYY-MM-DD.
 * Returns a short label fit for a large stat tile.
 */
function countdownValue(startDate: string): string {
  if (!startDate) return '—'
  const days = diffDaysFromToday(startDate)
  if (days === null) return '—'
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days === -1) return 'Yesterday'
  return `${Math.abs(days)} days`
}

function countdownHint(startDate: string): string {
  if (!startDate) return 'no start date set'
  const days = diffDaysFromToday(startDate)
  if (days === null) return ''
  if (days === 0) return 'opens today'
  if (days > 0) return `until ${formatDate(startDate)}`
  return `since ${formatDate(startDate)}`
}

function diffDaysFromToday(iso: string): number | null {
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - today.getTime()) / 86400000)
}
